import React, { useState, useRef, useEffect, useCallback } from 'react';
import { slideApiService } from '../services/slideApiService';
import {
  ImportPreviewResponse,
  ImportStatusResponse,
  ImportWarning,
  ImportInProgressError,
  ImportHistoryItem,
  ImportDeletionPreview,
  ImportDeletionStatus,
  RateLimitError,
} from '../types';

type Step = 'upload' | 'preview' | 'processing' | 'done';

const TERMINAL = ['COMPLETED', 'PARTIAL', 'FAILED', 'CANCELLED', 'EXPIRED'];
const isTerminal = (s: string | undefined) => !!s && TERMINAL.includes(s);

const C = {
  primary: 'var(--primary-light)',
  ink: 'var(--text-primary)',
  sub: 'var(--text-primary)',
  faint: 'var(--text-secondary)',
  border: 'var(--border-light)',
  bg: 'var(--bg-primary)',
  amber: 'var(--warning-text)',
  amberBg: 'var(--warning-bg)',
  amberBorder: 'var(--warning)',
  red: 'var(--error-text)',
  redBg: 'var(--error-bg)',
  green: 'var(--success-dark)',
};

interface Props {
  shopId: string;
}

const InventoryImportTab: React.FC<Props> = ({ shopId }) => {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [warnings, setWarnings] = useState<ImportWarning[]>([]);
  const [status, setStatus] = useState<ImportStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inProgressJobId, setInProgressJobId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false); // confirm/cancel in flight
  // True when this view restored an import that was already in flight (rather than one the user
  // just uploaded) — drives the "you left this unfinished" banner on the preview step.
  const [resumed, setResumed] = useState(false);
  const [restoring, setRestoring] = useState(true); // mount-time active-import lookup

  // Import history + "delete this import's slides" (start-over flow).
  const [history, setHistory] = useState<ImportHistoryItem[]>([]);
  const [confirmJob, setConfirmJob] = useState<ImportHistoryItem | null>(null);
  const [confirmCounts, setConfirmCounts] = useState<ImportDeletionPreview | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // Errors from the confirm dialog stay IN the dialog. The page-level banner sits at the top of
  // the view, but the Delete button is down in the history list - on a long list the feedback
  // could land off-screen and the click would look like it did nothing.
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [deletionJobId, setDeletionJobId] = useState<string | null>(null);
  const [deletion, setDeletion] = useState<ImportDeletionStatus | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setWarnings([]);
    setStatus(null);
    setError(null);
    setInProgressJobId(null);
    setResumed(false);
    setBusy(false);
    setConfirmJob(null);
    setConfirmCounts(null);
    setConfirmError(null);
  }, []);

  // Poll job status while processing.
  useEffect(() => {
    if (step !== 'processing' || !status) return;
    const jobId = status.jobId;
    let cancelled = false;
    const poll = async () => {
      try {
        const s = await slideApiService.getImportStatus(shopId, jobId);
        if (cancelled) return;
        setStatus(s);
        if (isTerminal(s.status)) {
          setStep('done');
          return;
        }
      } catch {
        // transient — keep polling
      }
      if (!cancelled) pollRef.current = setTimeout(poll, 2000);
    };
    pollRef.current = setTimeout(poll, 1500);
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, status?.jobId, shopId]);

  const onPickFile = (f: File | null) => {
    setError(null);
    setInProgressJobId(null);
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setInProgressJobId(null);
    try {
      const p = await slideApiService.uploadImport(shopId, file);
      const w = await slideApiService.getImportWarnings(shopId, p.jobId).catch(() => []);
      setPreview(p);
      setWarnings(w);
      setStep('preview');
    } catch (e) {
      if (ImportInProgressError.isImportInProgressError(e)) {
        setInProgressJobId(e.activeJobId || null);
        setError(e.message);
      } else if (RateLimitError.isRateLimitError(e)) {
        setError(e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Upload failed.');
      }
    } finally {
      setUploading(false);
    }
  };

  // Load an already-in-progress job (from a 409, or restored on mount) and route to the right step.
  const loadJob = useCallback(async (jobId: string) => {
    setBusy(true);
    setError(null);
    try {
      const s = await slideApiService.getImportStatus(shopId, jobId);
      setStatus(s);
      if (s.status === 'VALIDATED') {
        const w = await slideApiService.getImportWarnings(shopId, jobId).catch(() => []);
        setWarnings(w);
        setPreview({
          jobId: s.jobId, status: s.status, totalRows: s.totalRows, importableRows: s.importableRows,
          rejectedRows: s.rejectedRows, skippedForQuota: s.skippedForQuota, warningCount: s.warningCount,
          unrecognizedColumns: [],
        });
        setInProgressJobId(null);
        setStep('preview');
      } else if (isTerminal(s.status)) {
        setInProgressJobId(null);
        setStep('done');
      } else {
        setInProgressJobId(null);
        setStep('processing');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the in-progress import.');
    } finally {
      setBusy(false);
    }
  }, [shopId]);

  // On load, restore an import that is already in flight for this shop. Without this the user sees
  // an empty upload form and only learns about their unfinished import when the next upload 409s.
  // The endpoint mirrors the upload serialization guard, so "no active import" (204 -> null) means
  // a fresh upload will be accepted.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const active = await slideApiService.getActiveImport(shopId);
        if (cancelled || !active) return;
        setResumed(true);
        await loadJob(active.jobId);
      } catch {
        // Non-fatal: fall back to the upload form. A real conflict still surfaces on upload.
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, loadJob]);

  const handleConfirm = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const s = await slideApiService.confirmImport(shopId, preview.jobId);
      setStatus(s.jobId ? s : { ...(s as any), jobId: preview.jobId, status: 'CONFIRMED' });
      setStep('processing');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not confirm the import.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    const jobId = preview?.jobId || status?.jobId;
    if (!jobId) { reset(); return; }
    setBusy(true);
    try {
      await slideApiService.cancelImport(shopId, jobId);
    } catch {
      /* best-effort */
    }
    reset();
  };

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await slideApiService.getImportHistory(shopId));
    } catch {
      // Non-fatal: the history panel just stays empty. Importing itself is unaffected.
    }
  }, [shopId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // A run can be in flight without THIS view having started it: another tab, or a page refresh
  // mid-deletion. The deletionJobId poll below only covers a run this view kicked off, so without
  // this the row would sit on "Deleting…" indefinitely. Poll the history itself while any row is
  // non-terminal, and stop as soon as none are.
  const hasRunningDeletion = history.some(
    (h) => h.deletionStatus === 'REQUESTED' || h.deletionStatus === 'PROCESSING'
  );
  useEffect(() => {
    if (!hasRunningDeletion) return;
    const timer = setTimeout(() => { loadHistory(); }, 3000);
    return () => clearTimeout(timer);
  }, [hasRunningDeletion, history, loadHistory]);

  /** Open the confirm dialog, fetching the eligible/kept split for this import. */
  const openDeleteConfirm = async (item: ImportHistoryItem) => {
    setConfirmJob(item);
    setConfirmCounts(null);
    setConfirmError(null);
    setConfirmLoading(true);
    try {
      setConfirmCounts(await slideApiService.getDeletableSlides(shopId, item.jobId));
    } catch (e) {
      // Keep the dialog open and explain there, rather than closing it and reporting elsewhere.
      setConfirmError(e instanceof Error ? e.message : 'Could not check this import.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeleteImportedSlides = async () => {
    if (!confirmJob) return;
    const jobId = confirmJob.jobId;
    setBusy(true);
    setConfirmError(null);
    try {
      const d = await slideApiService.deleteImportedSlides(shopId, jobId);
      setDeletionJobId(jobId);
      setDeletion(d);
      setConfirmJob(null);
      setConfirmCounts(null);
    } catch (e) {
      // Most likely a 409 (another tab already started this cleanup). Report it here and leave the
      // dialog open so the user sees the answer where they asked the question.
      setConfirmError(e instanceof Error ? e.message : 'Could not start the deletion.');
      loadHistory(); // the other tab's run is now underway; refresh so the row reflects it
    } finally {
      setBusy(false);
    }
  };

  // Poll a deletion run until it finishes, then refresh history so the row shows the outcome.
  useEffect(() => {
    if (!deletionJobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      try {
        const d = await slideApiService.getSlideDeletionStatus(shopId, deletionJobId);
        if (cancelled) return;
        setDeletion(d);
        if (d.status === 'COMPLETED' || d.status === 'FAILED') {
          setDeletionJobId(null);
          loadHistory();
          return;
        }
      } catch {
        // transient - keep polling
      }
      if (!cancelled) timer = setTimeout(poll, 2000);
    };
    timer = setTimeout(poll, 1500);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [deletionJobId, shopId, loadHistory]);

  // --- render helpers ---

  const willImport = preview ? Math.max(0, preview.importableRows - preview.skippedForQuota) : 0;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '8px 4px' }}>
      <h2 style={{ color: C.ink, fontSize: 20, fontWeight: 700, margin: '4px 0 2px' }}>Import Inventory from CSV</h2>
      <p style={{ color: C.sub, fontSize: 14, margin: '0 0 16px' }}>
        Bulk-create slides from a spreadsheet. Each row becomes a slide with a placeholder image you can replace with real photos later.
      </p>

      {error && (
        <div style={{ background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 14 }}>
          {error}
          {inProgressJobId && (
            <button onClick={() => loadJob(inProgressJobId)} disabled={busy}
              style={{ marginLeft: 10, ...linkBtn }}>Go to it →</button>
          )}
        </div>
      )}

      {/* Avoid flashing an empty upload form before we know whether an import is already in flight. */}
      {restoring && (
        <div style={{ color: C.sub, fontSize: 14, padding: '10px 2px' }}>Checking for an unfinished import…</div>
      )}
      {!restoring && step === 'upload' && renderUpload()}
      {!restoring && step === 'preview' && preview && renderPreview()}
      {!restoring && (step === 'processing' || step === 'done') && status && renderProgress()}

      {/* History is only meaningful when not mid-import; showing it under a live preview competes
          with the decision the user is making right now. */}
      {!restoring && step === 'upload' && history.length > 0 && renderHistory()}
      {confirmJob && renderDeleteConfirm()}
    </div>
  );

  function renderHistory() {
    return (
      <div style={{ marginTop: 28, borderTop: `1px solid ${C.border}`, paddingTop: 18 }}>
        <h3 style={{ color: C.ink, fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>Past imports</h3>
        <p style={{ color: C.sub, fontSize: 13, margin: '0 0 12px' }}>
          Imported something you did not mean to? You can delete the slides an import created, as long
          as you have not added photos to them yet.
        </p>

        {deletion && (
          <div style={{ ...noticeBox, background: C.bg, borderColor: C.border, color: C.ink }}>
            {deletion.status === 'COMPLETED' ? (
              <>
                Deleted {deletion.deleted} {deletion.deleted === 1 ? 'slide' : 'slides'}
                {deletion.kept > 0 && <> and kept {deletion.kept} that you have added photos to</>}.
                {' '}It can take a few minutes for your slide count to catch up.
              </>
            ) : deletion.status === 'FAILED' ? (
              <>Deletion failed. {deletion.errorMessage}</>
            ) : (
              <>Deleting slides… {deletion.deleted} removed so far.</>
            )}
          </div>
        )}

        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {history.map((item, i) => {
            const running = item.deletionStatus === 'REQUESTED' || item.deletionStatus === 'PROCESSING'
              || deletionJobId === item.jobId;
            const alreadyDeleted = item.deletionStatus === 'COMPLETED';
            // Every slide the import created should end up either deleted or kept. When the tallies
            // add up, nothing can remain and the run is genuinely finished. When they fall short the
            // run may have stopped early - but the user may also have deleted some of those slides by
            // hand, so this is a reason to offer another look, not proof that anything was missed.
            const fullyAccounted = item.deletedCount + item.keptCount >= item.created;
            const finished = alreadyDeleted && fullyAccounted;
            return (
              <div key={item.jobId} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', fontSize: 13,
                borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.ink, fontWeight: 600 }}>
                    {formatWhen(item.createdAt)}
                    <span style={{ color: C.faint, fontWeight: 400 }}> · {item.created} {item.created === 1 ? 'slide' : 'slides'} created</span>
                  </div>
                  <div style={{ color: C.faint, fontFamily: 'monospace', fontSize: 11 }}>{item.jobId}</div>
                  {alreadyDeleted && (
                    <div style={{ color: C.sub }}>
                      {item.deletedCount} deleted{item.keptCount > 0 && <>, {item.keptCount} kept</>}
                      {!fullyAccounted && (
                        <span style={{ color: C.amber }}>
                          {' '}· some slides may remain. Check again.
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {/* Disabled only when the run demonstrably finished the job (tallies account for every
                    slide it created). A run CAN complete with slides still deletable - a chunk that
                    failed, or a claimed range whose execution died - and in that case the button stays
                    live so the user can finish it. The dialog's preflight remains the authoritative
                    check either way. */}
                <button
                  onClick={() => openDeleteConfirm(item)}
                  disabled={running || finished || confirmLoading}
                  style={{ ...secondaryBtn, padding: '6px 12px', fontSize: 13,
                           color: running || finished ? C.faint : C.red,
                           cursor: running || finished ? 'not-allowed' : 'pointer' }}>
                  {running ? 'Deleting…' : finished ? 'Slides deleted' : alreadyDeleted ? 'Delete remaining' : 'Delete slides'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderDeleteConfirm() {
    if (!confirmJob) return null;
    const counts = confirmCounts;
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000,
      }}>
        <div style={{ background: 'var(--bg-primary)', borderRadius: 10, padding: 20, maxWidth: 480, width: '100%' }}>
          <h3 style={{ color: C.ink, fontSize: 17, fontWeight: 700, margin: '0 0 10px' }}>
            Delete slides from this import?
          </h3>

          {confirmError ? (
            <div style={{ background: C.redBg, border: `1px solid ${C.red}33`, color: C.red,
                          borderRadius: 8, padding: '10px 12px', fontSize: 14 }}>
              {confirmError}
            </div>
          ) : confirmLoading || !counts ? (
            <p style={{ color: C.sub, fontSize: 14 }}>Checking which slides can be deleted…</p>
          ) : counts.deletable === 0 ? (
            <p style={{ color: C.ink, fontSize: 14, lineHeight: 1.5 }}>
              Nothing can be deleted from this import. You have added photos to all
              {' '}{counts.kept} of its {counts.kept === 1 ? 'slide' : 'slides'}, so they are being kept.
            </p>
          ) : (
            <div style={{ color: C.ink, fontSize: 14, lineHeight: 1.55 }}>
              <p style={{ margin: '0 0 8px' }}>
                <strong>{counts.deletable}</strong> of this import&rsquo;s{' '}
                {counts.deletable + counts.kept} slides still have the placeholder image and will be deleted.
              </p>
              {counts.kept > 0 && (
                <p style={{ margin: '0 0 8px' }}>
                  <strong>{counts.kept}</strong> will be kept because you have added photos to them.
                </p>
              )}
              {/* State the rule outright: a slide the user edited but never photographed still goes. */}
              <p style={{ margin: '0 0 8px', color: C.amber }}>
                Slides are deleted even if you have edited their name, price or notes. This cannot be undone.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setConfirmJob(null); setConfirmCounts(null); setConfirmError(null); }}
              disabled={busy}
              style={secondaryBtn}>
              {confirmError ? 'Close' : 'Cancel'}
            </button>
            {/* With an error showing, Close is the only sensible action - a greyed-out Delete beside
                the message just invites clicking. */}
            {!confirmError && (
              <button
                onClick={handleDeleteImportedSlides}
                disabled={busy || confirmLoading || !counts || counts.deletable === 0}
                style={{ ...primaryBtn(busy || confirmLoading || !counts || counts.deletable === 0),
                         background: busy || !counts || counts.deletable === 0 ? 'var(--error)' : C.red }}>
                {busy ? 'Starting…' : `Delete ${counts ? counts.deletable : ''} slides`}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderUpload() {
    return (
      <div>
        <div style={{ marginBottom: 12, fontSize: 13, color: C.sub }}>
          Only <strong>name</strong> is required per row.{' '}
          <a href="/templates/inventory-template.csv" download style={{ color: C.primary, fontWeight: 600 }}>
            Download the CSV template
          </a>{' '}
          to see all columns. Up to 5,000 rows / 5 MB per file. Split larger inventories into multiple imports.
        </div>

        <div style={{ marginBottom: 12, fontSize: 13, color: C.sub }}>
          Your <strong>description</strong> is saved to each slide’s internal notes rather than its
          description. That keeps your wording safe: once you add real photos and run Quick Analyze, it
          writes an AI description for you, and you can copy anything you want back from the notes.
        </div>

        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onPickFile(e.dataTransfer.files?.[0] || null); }}
          style={{
            border: `2px dashed ${C.border}`, borderRadius: 10, padding: '28px 16px', textAlign: 'center',
            cursor: 'pointer', background: C.bg,
          }}
        >
          <div style={{ fontSize: 15, color: C.ink, fontWeight: 600 }}>
            {file ? file.name : 'Drop a .csv here, or click to choose'}
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>
            {file ? `${(file.size / 1024).toFixed(0)} KB` : 'CSV only'}
          </div>
          <input
            ref={fileInputRef} type="file" accept="text/csv,.csv" style={{ display: 'none' }}
            onChange={(e) => onPickFile(e.target.files?.[0] || null)}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <button onClick={handleUpload} disabled={!file || uploading} style={primaryBtn(!file || uploading)}>
            {uploading ? 'Validating…' : 'Upload & Preview'}
          </button>
        </div>
      </div>
    );
  }

  /**
   * Why nothing will import, and what the user can actually do about it. A slide-limit problem is
   * not fixable in the spreadsheet, so telling those users to "fix the file and re-upload" sends
   * them in circles.
   */
  function nothingToImportMessage() {
    if (!preview) return null;
    const overLimit = preview.skippedForQuota > 0;
    const missingName = preview.rejectedRows > 0;

    if (overLimit && !missingName) {
      return (
        <>
          Nothing will import. {preview.skippedForQuota === 1 ? 'This row is' : 'These rows are'} over
          your slide limit. Delete slides you no longer need, or upgrade your plan in{' '}
          <strong>My Shop &gt; Subscription</strong>, then re-upload.
        </>
      );
    }
    if (missingName && !overLimit) {
      return <>Nothing will import. Every row is missing a name. Fix the file and re-upload.</>;
    }
    if (missingName && overLimit) {
      return (
        <>
          Nothing will import. Some rows are missing a name and the rest are over your slide limit.
          Add the missing names, then delete slides you no longer need or upgrade your plan in{' '}
          <strong>My Shop &gt; Subscription</strong>, and re-upload.
        </>
      );
    }
    return <>Nothing will import. The file has no rows to import.</>;
  }

  function renderPreview() {
    if (!preview) return null;
    return (
      <div>
        {resumed && (
          <div style={{ background: C.amberBg, border: `1px solid ${C.amber}55`, color: C.ink,
                        borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 14 }}>
            You have an unfinished CSV import for this shop. Review it below and either{' '}
            <strong>import</strong> the slides or <strong>cancel</strong> it. You can’t start another
            import until this one is finished or cancelled.
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          {stat('Rows in file', preview.totalRows, C.ink)}
          {stat('Will import', willImport, C.green)}
          {preview.rejectedRows > 0 && stat('Rejected (no name)', preview.rejectedRows, C.red)}
          {preview.skippedForQuota > 0 && stat('Over slide limit (skipped)', preview.skippedForQuota, C.amber)}
          {stat('Warnings', preview.warningCount, preview.warningCount > 0 ? C.amber : C.sub)}
        </div>

        {preview.unrecognizedColumns.length > 0 && (
          <div style={noticeBox}>
            Unrecognized columns ignored: {preview.unrecognizedColumns.map((c) => `"${c}"`).join(', ')}
          </div>
        )}

        {/* The fix depends on the cause: bad data is fixed in the spreadsheet, but a slide-limit
            problem cannot be, so point those users at freeing up space or upgrading instead. */}
        {willImport === 0 && (
          <div style={{ ...noticeBox, background: C.redBg, borderColor: `${C.red}44`, color: C.red }}>
            {nothingToImportMessage()}
          </div>
        )}

        {/* Some rows will import, but not all of them. Same guidance, milder framing. */}
        {willImport > 0 && preview.skippedForQuota > 0 && (
          <div style={noticeBox}>
            {preview.skippedForQuota} {preview.skippedForQuota === 1 ? 'row is' : 'rows are'} over your
            slide limit and will be skipped. To import {preview.skippedForQuota === 1 ? 'it' : 'them'},
            delete slides you no longer need or upgrade your plan in <strong>My Shop &gt; Subscription</strong>,
            then re-upload.
          </div>
        )}

        {warnings.length > 0 && (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
            <div style={{ background: C.bg, padding: '8px 12px', fontSize: 13, fontWeight: 600, color: C.ink, borderBottom: `1px solid ${C.border}` }}>
              Warnings ({warnings.length})
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {warnings.map((w, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '7px 12px', fontSize: 13, borderBottom: i < warnings.length - 1 ? `1px solid ${C.bg}` : 'none' }}>
                  <span style={{ color: C.faint, minWidth: 52 }}>{w.rowNumber > 0 ? `Row ${w.rowNumber}` : 'File'}</span>
                  <span style={{ color: C.sub }}>{w.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleConfirm} disabled={busy || willImport === 0} style={primaryBtn(busy || willImport === 0)}>
            {busy ? 'Starting…' : `Import ${willImport} slide${willImport === 1 ? '' : 's'}`}
          </button>
          <button onClick={handleCancel} disabled={busy} style={secondaryBtn}>Cancel</button>
        </div>
      </div>
    );
  }

  function renderProgress() {
    if (!status) return null;
    const done = step === 'done';
    const pct = status.totalRows > 0 ? Math.min(100, Math.round((status.created / Math.max(1, status.importableRows - status.skippedForQuota)) * 100)) : 0;
    const label = statusLabel(status.status);
    return (
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: label.color, marginBottom: 8 }}>{label.text}</div>
        <div style={{ fontSize: 14, color: C.sub, marginBottom: 12 }}>
          Created {status.created} slide{status.created === 1 ? '' : 's'}
          {status.importableRows > 0 && ` of ${Math.max(0, status.importableRows - status.skippedForQuota)}`}
          {status.skippedForQuota > 0 && ` · ${status.skippedForQuota} skipped (slide limit)`}
        </div>
        {!done && (
          <div style={{ height: 8, background: C.border, borderRadius: 5, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: C.primary, transition: 'width 0.4s' }} />
          </div>
        )}
        {status.status === 'FAILED' && status.errorMessage && (
          <div style={{ ...noticeBox, background: C.redBg, borderColor: `${C.red}44`, color: C.red }}>{status.errorMessage}</div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          {done ? (
            <button onClick={reset} style={primaryBtn(false)}>Import another</button>
          ) : (
            <button onClick={handleCancel} disabled={busy} style={secondaryBtn}>Cancel import</button>
          )}
        </div>
      </div>
    );
  }

  function stat(label: string, value: number, color: string) {
    return (
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', minWidth: 110, background: 'var(--bg-primary)' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
        <div style={{ fontSize: 12, color: C.sub }}>{label}</div>
      </div>
    );
  }
};

function statusLabel(s: string): { text: string; color: string } {
  switch (s) {
    case 'COMPLETED': return { text: 'Import complete', color: C.green };
    case 'PARTIAL': return { text: 'Import partially complete (slide limit reached)', color: C.amber };
    case 'CANCELLED': return { text: 'Import cancelled', color: C.sub };
    case 'EXPIRED': return { text: 'Import expired', color: C.sub };
    case 'FAILED': return { text: 'Import failed', color: C.red };
    default: return { text: 'Importing…', color: C.primary };
  }
}

/** Import timestamps are ISO-8601 UTC; show them in the viewer's local time. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const noticeBox: React.CSSProperties = {
  background: C.amberBg, border: `1px solid ${C.amberBorder}`, color: C.amber,
  borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13,
};

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: C.red, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0,
};

const secondaryBtn: React.CSSProperties = {
  background: 'var(--bg-primary)', color: C.ink, border: `1px solid ${C.border}`, borderRadius: 8,
  padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

function primaryBtn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'var(--disabled-bg)' : C.primary,
    color: disabled ? 'var(--disabled)' : 'var(--text-inverse)', border: 'none', borderRadius: 8,
    padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

export default InventoryImportTab;
